import { useEffect, useState } from "react";
import { api } from "@/api/client";

type Geo = { country: string | null; country_code: string | null; city: string | null };
type Cache = Record<string, Geo | null>;

let _cache: Cache = {};
let _pending: string[] = [];
let _timer: number | null = null;
const _subs = new Set<() => void>();

function scheduleFlush() {
  if (_timer !== null) return;
  _timer = window.setTimeout(async () => {
    _timer = null;
    const batch = [..._pending];
    _pending = [];
    if (batch.length === 0) return;
    try {
      const r = await api<{ results: Record<string, Geo | null> }>(
        "/api/admin/geo/lookup",
        { method: "POST", json: { ips: batch } }
      );
      Object.assign(_cache, r.results);
    } catch {
      batch.forEach((ip) => {
        if (!(ip in _cache)) _cache[ip] = null;
      });
    }
    _subs.forEach((fn) => fn());
  }, 250) as unknown as number;
}

function request(ip: string) {
  if (ip in _cache) return;
  // mark as "in-flight" so duplicates don't queue
  _cache[ip] = null;
  _pending.push(ip);
  scheduleFlush();
}

/** Renders an IP + country/city pill once geolocated. Shared cache across
 *  all cells on the page so a single batch endpoint handles the lot.
 */
export function IpCell({ ip }: { ip: string | null | undefined }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const sub = () => setTick((n) => n + 1);
    _subs.add(sub);
    if (ip) request(ip);
    return () => {
      _subs.delete(sub);
    };
  }, [ip]);
  if (!ip) return <span className="text-slate-500">—</span>;
  const g = _cache[ip];
  return (
    <span className="inline-flex items-baseline gap-1.5 mono text-xs">
      <span className="text-slate-400">{ip}</span>
      {g && g.country_code && (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-400"
          title={[g.city, g.country].filter(Boolean).join(", ")}
        >
          {g.country_code}
        </span>
      )}
    </span>
  );
}
